import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { ContainerState } from './types';

// The initial state of the templates container
export const initialState: ContainerState = {
  emailTemplates: { data: {}, loading: false, error: null },
  lastRefreshTrigger: 0,
  generateTemplates: { loading: false, error: null },
  generateInProgress: false,
  generateStartedDealerId: null,
  generateProgress: { current: 0, total: 1 },
};

const emailsGridSlice = createSlice({
  name: 'emailsGridState',
  initialState,
  reducers: {
    requestEmailTemplates(state, action: PayloadAction<any>) {
      state.emailTemplates.data = {};
      state.emailTemplates.loading = true;
      state.emailTemplates.error = null;
    },
    requestEmailTemplatesSuccess(state, action: PayloadAction<any>) {
      state.emailTemplates.data = action.payload;
      state.emailTemplates.loading = false;
    },
    requestEmailTemplatesError(state, action: PayloadAction<any>) {
      state.emailTemplates.error = action.payload;
      state.emailTemplates.loading = false;
    },
    requestDeleteEmailTemplate(state, action: PayloadAction<any>) {
      state.emailTemplates.loading = true;
      state.emailTemplates.error = null;
    },
    requestDeleteEmailTemplateSuccess(state) {
      state.emailTemplates.loading = false;
      state.lastRefreshTrigger = (state.lastRefreshTrigger || 0) + 1;
    },
    requestDeleteEmailTemplateError(state, action: PayloadAction<any>) {
      state.emailTemplates.error = action.payload;
      state.emailTemplates.loading = false;
    },
    requestUpdateEmailTemplate(state, action: PayloadAction<any>) {
      state.emailTemplates.loading = true;
      state.emailTemplates.error = null;
    },
    requestUpdateEmailTemplateSuccess(state) {
      state.emailTemplates.loading = false;
      state.lastRefreshTrigger = (state.lastRefreshTrigger || 0) + 1;
    },
    requestUpdateEmailTemplateError(state, action: PayloadAction<any>) {
      state.emailTemplates.error = action.payload;
      state.emailTemplates.loading = false;
    },
    requestApproveEmailTemplate(
      state,
      action: PayloadAction<{ dealerId: string; emailTemplateId: string }>,
    ) {
      state.emailTemplates.loading = true;
      state.emailTemplates.error = null;
    },
    requestApproveEmailTemplateSuccess(state) {
      state.emailTemplates.loading = false;
      state.lastRefreshTrigger = (state.lastRefreshTrigger || 0) + 1;
    },
    requestApproveEmailTemplateError(state, action: PayloadAction<any>) {
      state.emailTemplates.error = action.payload;
      state.emailTemplates.loading = false;
    },
    requestRejectEmailTemplate(
      state,
      action: PayloadAction<{ dealerId: string; emailTemplateId: string }>,
    ) {
      state.emailTemplates.loading = true;
      state.emailTemplates.error = null;
    },
    requestRejectEmailTemplateSuccess(state) {
      state.emailTemplates.loading = false;
      state.lastRefreshTrigger = (state.lastRefreshTrigger || 0) + 1;
    },
    requestRejectEmailTemplateError(state, action: PayloadAction<any>) {
      state.emailTemplates.error = action.payload;
      state.emailTemplates.loading = false;
    },
    requestGenerateEmailTemplates(state, action: PayloadAction<any>) {
      state.generateTemplates.loading = true;
      state.generateTemplates.error = null;
      state.generateInProgress = true;
      state.generateStartedDealerId = action.payload?.dealerId ?? null;
    },
    requestGenerateEmailTemplatesSuccess(state) {
      state.generateTemplates.loading = false;
      state.generateTemplates.error = null;
      // Do not increment lastRefreshTrigger here – grid refreshes only on socket template_generation_completed (via hook’s onTemplateUpdate)
    },
    requestGenerateEmailTemplatesError(state, action: PayloadAction<any>) {
      state.generateTemplates.loading = false;
      state.generateTemplates.error = action.payload;
      state.generateInProgress = false;
      state.generateStartedDealerId = null;
    },
    setGenerateCompleted(state) {
      state.generateInProgress = false;
      state.generateStartedDealerId = null;
      state.generateProgress = { current: 0, total: 1 };
    },
    setGenerateProgress(state, action: PayloadAction<{ current: number; total: number }>) {
      state.generateProgress = action.payload;
    },
    requestExpireEmailTemplates(state, action: PayloadAction<{ dealerId: string; ids: string[] }>) {
      state.emailTemplates.loading = true;
      state.emailTemplates.error = null;
    },
    requestExpireEmailTemplatesSuccess(state) {
      state.emailTemplates.loading = false;
      state.lastRefreshTrigger = (state.lastRefreshTrigger || 0) + 1;
    },
    requestExpireEmailTemplatesError(state, action: PayloadAction<any>) {
      state.emailTemplates.error = action.payload;
      state.emailTemplates.loading = false;
    },
    requestDeleteEmailTemplates(state, action: PayloadAction<{ dealerId: string; ids: string[] }>) {
      state.emailTemplates.loading = true;
      state.emailTemplates.error = null;
    },
    requestDeleteEmailTemplatesSuccess(state) {
      state.emailTemplates.loading = false;
      state.lastRefreshTrigger = (state.lastRefreshTrigger || 0) + 1;
    },
    requestDeleteEmailTemplatesError(state, action: PayloadAction<any>) {
      state.emailTemplates.error = action.payload;
      state.emailTemplates.loading = false;
    },
    requestBulkApproveEmailTemplates(
      state,
      action: PayloadAction<{ dealerId: string; ids: string[] }>,
    ) {
      state.emailTemplates.loading = true;
      state.emailTemplates.error = null;
    },
    requestBulkApproveEmailTemplatesSuccess(state) {
      state.emailTemplates.loading = false;
      state.lastRefreshTrigger = (state.lastRefreshTrigger || 0) + 1;
    },
    requestBulkApproveEmailTemplatesError(state, action: PayloadAction<any>) {
      state.emailTemplates.error = action.payload;
      state.emailTemplates.loading = false;
    },
    requestBulkRejectEmailTemplates(
      state,
      action: PayloadAction<{ dealerId: string; ids: string[] }>,
    ) {
      state.emailTemplates.loading = true;
      state.emailTemplates.error = null;
    },
    requestBulkRejectEmailTemplatesSuccess(state) {
      state.emailTemplates.loading = false;
      state.lastRefreshTrigger = (state.lastRefreshTrigger || 0) + 1;
    },
    requestBulkRejectEmailTemplatesError(state, action: PayloadAction<any>) {
      state.emailTemplates.error = action.payload;
      state.emailTemplates.loading = false;
    },
    forceRefreshEmailTemplates(state) {
      state.lastRefreshTrigger = (state.lastRefreshTrigger || 0) + 1;
    },
  },
});

export const {
  actions: emailsGridActions,
  reducer,
  name: sliceKey,
} = emailsGridSlice;
